'use client'

/**
 * DollarCostAverageCalculator: free tool for "dollar cost averaging calculator / DCA calculator". You
 * invest a fixed amount at each of several prices; it computes total invested, total shares, the blended
 * average cost, and (with a current price) your value and return. Shows how a fixed-dollar schedule buys
 * more shares when cheaper, so the average cost lands below the simple average of the prices. Pure client math.
 */

import { useMemo, useState } from 'react'
import CalcField from '@/components/calc/CalcField'

const fmtUSD = (n: number): string => (isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 4 }) : '$0.00')
const fmtUSD2 = (n: number): string => (isFinite(n) ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }) : '$0.00')
const fmtPct = (n: number): string => `${isFinite(n) && n >= 0 ? '+' : ''}${isFinite(n) ? n.toFixed(2) : '0.00'}%`

export default function DollarCostAverageCalculator() {
  const [amount, setAmount] = useState('500')
  const [prices, setPrices] = useState('10, 8, 12, 9, 7')
  const [current, setCurrent] = useState('')

  const r = useMemo(() => {
    const amt = parseFloat(amount) || 0
    const list = prices.split(/[,\s]+/).map((p) => parseFloat(p)).filter((p) => isFinite(p) && p > 0)
    const buys = list.length
    const totalInvested = amt * buys
    const totalShares = list.reduce((s, p) => s + (p > 0 ? amt / p : 0), 0)
    const avg = totalShares > 0 ? totalInvested / totalShares : 0
    const simpleAvg = buys > 0 ? list.reduce((s, p) => s + p, 0) / buys : 0
    const cur = parseFloat(current)
    const hasCurrent = isFinite(cur) && cur > 0
    const value = hasCurrent ? cur * totalShares : 0
    const pnl = hasCurrent ? value - totalInvested : 0
    const pnlPct = hasCurrent && totalInvested > 0 ? (pnl / totalInvested) * 100 : 0
    const invalid = amt <= 0 || buys === 0
    return { amt, buys, totalInvested, totalShares, avg, simpleAvg, hasCurrent, value, pnl, pnlPct, invalid }
  }, [amount, prices, current])

  return (
    <div className="rounded-2xl border border-zinc-800 bg-zinc-900/40 p-4 sm:p-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <CalcField label="Amount invested each time" value={amount} onChange={setAmount} prefix="$" step="10" hint="The fixed dollar amount per buy" />
        <CalcField label="Current price (optional)" value={current} onChange={setCurrent} prefix="$" hint="Add it to see value and return" />
      </div>
      <label className="mt-4 block">
        <span className="mb-1 block text-[13px] font-semibold text-zinc-300">Prices you bought at</span>
        <input
          type="text" value={prices} onChange={(e) => setPrices(e.target.value)}
          placeholder="e.g. 10, 8, 12, 9, 7"
          className="w-full rounded-xl border border-zinc-700 bg-zinc-900/70 px-3 py-2.5 text-[16px] font-bold tabular-nums text-white outline-none focus:border-emerald-500/60 placeholder:font-normal placeholder:text-zinc-600"
        />
        <span className="mt-1 block text-[11px] text-zinc-500">Separate each purchase price with a comma. One buy of the amount above happens at each price.</span>
      </label>

      {r.invalid ? (
        <div className="mt-5 rounded-xl border border-zinc-700/70 bg-zinc-900/60 px-4 py-6 text-center text-sm text-zinc-400">
          Enter an amount and at least one price to run the dollar-cost-average.
        </div>
      ) : (
        <>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/[0.07] px-5 py-4">
              <div className="text-[12px] font-bold uppercase tracking-wider text-emerald-300">Average cost per share</div>
              <div className="mt-1 text-[34px] font-black leading-none tabular-nums text-white">{fmtUSD(r.avg)}</div>
              <div className="mt-1.5 text-[13px] text-zinc-400">{r.totalShares.toLocaleString('en-US', { maximumFractionDigits: 2 })} shares from {r.buys} buys · {fmtUSD2(r.totalInvested)} invested</div>
            </div>
            {r.hasCurrent ? (
              <div className="rounded-2xl border border-zinc-700/70 bg-zinc-900/60 px-5 py-4">
                <div className="text-[12px] font-bold uppercase tracking-wider text-zinc-400">At the current price</div>
                <div className="mt-1.5 space-y-1 text-[14px] tabular-nums text-zinc-300">
                  <div className="flex justify-between"><span className="text-zinc-500">Value</span><span className="font-bold">{fmtUSD2(r.value)}</span></div>
                  <div className="flex justify-between"><span className="text-zinc-500">Profit / loss</span><span className="font-bold" style={{ color: r.pnl >= 0 ? '#34d399' : '#f43f5e' }}>{fmtUSD2(r.pnl)} ({fmtPct(r.pnlPct)})</span></div>
                </div>
              </div>
            ) : (
              <div className="rounded-2xl border border-zinc-700/70 bg-zinc-900/60 px-5 py-4">
                <div className="text-[12px] font-bold uppercase tracking-wider text-zinc-400">Dollar-cost-averaging edge</div>
                <div className="mt-1.5 text-[13px] text-zinc-300">
                  Your average cost is <span className="font-bold text-emerald-300">{fmtUSD(r.avg)}</span> versus a simple price average of{' '}
                  <span className="font-bold text-zinc-200">{fmtUSD(r.simpleAvg)}</span>. Fixed-dollar buying purchases more shares when the price is lower, which pulls your cost below the plain average.
                </div>
              </div>
            )}
          </div>
          <p className="mt-3 text-[11px] leading-relaxed text-zinc-600">
            Estimates exclude fees. Dollar-cost averaging spreads risk over time but does not guarantee a profit. Educational tool, not investment advice.
          </p>
        </>
      )}
    </div>
  )
}
